// ─── suggestResultsCall ──────────────────────────────────────────────────
// 2026-07-26 — Module « Préparer l'appel résultats » : à partir des
// analyses SAISIES ET CLASSÉES PAR ANISSA (jamais par l'IA), propose des
// points-clés PRIVÉS pour préparer son appel téléphonique à la cliente.
// Cadrage = ses réponses écrites (docs/SPEC-appel-resultats.md).
//
// GARDE-FOUS :
// - L'IA ORGANISE et FORMULE. Elle ne classe rien, n'invente aucun seuil,
//   aucune valeur, aucun symptôme. « Ce qui ressort » = les statuts posés
//   par Anissa à la saisie (eleve / sous_optimal / surveiller).
// - Interdictions absolues d'Anissa (Q6) codées ici : jamais de diagnostic,
//   de certitude, d'alarmisme, jamais toucher à un traitement médical.
//   Un SCAN DE SORTIE fail-closed rejette TOUT le texte à la moindre
//   formulation interdite — rien n'est filtré en douce.
// - Sortie = points-clés affichés à Anissa uniquement. Jamais envoyés à la
//   cliente, jamais sauvegardés automatiquement.
// - Profil sensible (sans-chiffres / TCA) : les points destinés à
//   l'échange ne contiennent AUCUN chiffre ; zéro jugement poids/aliments.

import { callClaude, ClaudeApiError } from './anthropic';
import { hasEnoughForResultsCall } from './buildResultsCallData';

// Formulations interdites (réponse Q6 d'Anissa + vocabulaire doctrine
// maison + variantes tutoiement/vouvoiement/orthographe, revue
// clinique-guard R2 du 2026-07-26). Scan en minuscules.
export const FORBIDDEN_PHRASES = [
  // Certitude / preuve
  'prouve que',
  'forcément',
  'forcement', // variante sans accent
  'aucun risque',
  'aucun doute',
  'c\'est certain',
  'il est certain',
  // Diagnostic (le prompt impose de ne jamais écrire le mot, même prudemment)
  'diagnosti', // diagnostic, diagnostiqué, diagnostiquer…
  // Vocabulaire alarmiste interdit par la doctrine (« hors de la plage
  // retenue », jamais « anormal »)
  'anormal', // anormale, anormalement…
  'pathologique',
  'inquiétant',
  'grave',
  // Traitement médical : ni arrêt, ni changement de dose, ni substitution
  'arrêter ton traitement',
  'arrêter votre traitement',
  'arrête ton traitement',
  'arrêtez votre traitement',
  'stopper ton traitement',
  'stopper votre traitement',
  'réduire ton traitement',
  'réduire votre traitement',
  'diminuer ton traitement',
  'diminuer votre traitement',
  'remplacer ton traitement',
  'remplacer votre traitement',
  'changer la dose',
  'modifier la dose',
  'tu dois arrêter',
  'vous devez arrêter',
];

// Formulations PRUDENTES légitimes retirées AVANT le scan : elles
// contiennent un mot interdit mais dans un usage rassurant/prudent
// (« ce n'est pas forcément lié », « rien de grave »). Liste courte et
// explicite — tout le reste reste fail-closed.
const PRUDENT_EXEMPTIONS = [
  /pas forcément/g,
  /pas forcement/g,
  /rien de grave/g,
  /pas grave/g,
  /rien d'inquiétant/g,
  /pas inquiétant/g,
];

/** Renvoie les formulations interdites trouvées dans un texte (minuscules). */
export function findForbiddenPhrases(text) {
  let low = (text || '').toLowerCase();
  for (const rx of PRUDENT_EXEMPTIONS) low = low.replace(rx, '');
  return FORBIDDEN_PHRASES.filter((p) => low.includes(p));
}

/** Aplatit récursivement toutes les strings d'une valeur JSON. */
export function flattenStrings(value) {
  if (typeof value === 'string') return [value];
  if (Array.isArray(value)) return value.flatMap(flattenStrings);
  if (value && typeof value === 'object') return Object.values(value).flatMap(flattenStrings);
  return [];
}

/**
 * Vérifie TOUTE la sortie IA (fail-closed). Revue clinique-guard R4 :
 * pour un profil sensible (sans-chiffres / TCA / flag illisible), le
 * moindre chiffre dans les points d'échange rejette la sortie entière —
 * c'est ce qu'Anissa va dire à voix haute au téléphone.
 * @returns liste des violations (vide = sortie acceptable)
 */
export function findOutputViolations(result, { sensitive = false } = {}) {
  const texts = flattenStrings(result);
  const hits = [...new Set(texts.flatMap((t) => findForbiddenPhrases(t)))];
  if (sensitive && texts.some((t) => /\d/.test(t))) {
    hits.push('chiffre présent (profil sensible : points d\'échange sans chiffres)');
  }
  return hits;
}

const SYSTEM_PROMPT = `Tu aides Anissa à préparer un APPEL TÉLÉPHONIQUE à une cliente qui vient de recevoir ses analyses (Anissa n'est ni médecin ni biologiste). Ce que tu produis est PRIVÉ : seule Anissa le lit, rien ne part vers la cliente. Anissa a déjà lu et classé chaque analyse elle-même (Optimal / À surveiller / Sous-optimal / Élevé). Ton rôle : ORGANISER ses propres classements en points-clés d'appel. Tu ne classes rien, tu ne réinterprètes rien.

DÉROULÉ DE SON APPEL (respecte cet ordre dans ta sortie)
1. Elle commence par rassurer et donner une vision globale.
2. Puis elle explique ce qui ressort, sans dramatiser.
3. Elle termine par les priorités concrètes et la suite de l'accompagnement.

CE QUE TU PRODUIS (JSON strict, aucune autre sortie)
{
  "vision_globale": "2-3 phrases : le message d'ouverture rassurant et honnête, vision d'ensemble",
  "a_rassurer": ["point à dire pour rassurer (ce qui est optimal et que la cliente pourrait craindre à tort)"],
  "par_theme": [{ "theme": "nom du thème", "points": ["point-clé à expliquer, langage simple de nutritionniste"] }],
  "pistes_plan": ["lien possible entre un résultat et un axe de l'accompagnement — TOUJOURS formulé comme une piste, se termine par « — à valider »"],
  "questions_a_poser": ["question concrète à poser à la cliente pendant l'appel"],
  "sujets_delicats": [{ "sujet": "sujet sensible à aborder avec précaution", "formulations": ["2-3 façons de le dire, dans la voix d'Anissa"] }]
}

STYLE — POINTS-CLÉS, PAS UN SCRIPT
- Des points courts et concrets qu'Anissa consulte en parlant. PAS de texte à réciter.
- Exception : "sujets_delicats" propose 2-3 formulations prêtes, uniquement pour les sujets délicats.
- Voix d'Anissa pour ces formulations : elle TUTOIE ses clientes, parle simplement, chaleureusement, sans jargon de laboratoire, jamais alarmiste.

VOCABULAIRE OBLIGATOIRE
- « hors de la plage retenue », « à surveiller », « sous-optimal » — JAMAIS « anormal », « pathologique », « inquiétant », « grave » (même niés : dis « tout va bien de ce côté » plutôt que « rien de grave »).
- N'écris JAMAIS les mots « diagnostic » ou « prouve », même dans une phrase prudente : dis « c'est au médecin de se prononcer », « rien n'indique ».
- Verbes d'accompagnement : soutenir, favoriser, rééquilibrer, prioriser. JAMAIS : soigner, guérir, traiter, détecter.
- Si un élément sort du champ d'une nutritionniste : le point dit sobrement qu'Anissa oriente vers le médecin traitant (« c'est un point à voir avec ton médecin »).

INTERDICTIONS ABSOLUES (la moindre violation invalide TOUTE ta sortie)
- JAMAIS de diagnostic, de nom de maladie affirmé, de conclusion certaine.
- JAMAIS de formulation alarmiste ni de fausse garantie : pas de « prouve que », « forcément », « aucun risque », « aucun doute », « c'est certain ».
- JAMAIS toucher à un traitement médical : ni arrêt, ni changement de dose, ni substitution. Un traitement en cours relève du médecin, point.
- JAMAIS présenter une hypothèse comme une certitude : « pourrait contribuer à », « piste à explorer », « semble cohérent avec » — jamais plus affirmatif.
- N'invente AUCUNE donnée : uniquement les analyses, statuts et synthèses fournis. Une info absente n'existe pas.
- Les pistes de plan sont des PISTES : la décision sur les axes appartient à Anissa seule.

FORMAT : réponds UNIQUEMENT avec le JSON, sans markdown, sans préambule, sans commentaire. Français.`;

/**
 * Construit le message utilisateur à partir des données du builder.
 * Pure : testable sans I/O.
 */
export function buildResultsCallPrompt(data) {
  const lines = [];
  if (data.prenom) lines.push(`Cliente : ${data.prenom}`);
  if (data.sensitive) {
    lines.push(
      `PROFIL SENSIBLE (présentation sans chiffres et/ou profil à risque) : tes points destinés à l'échange ne contiennent AUCUN chiffre, aucune valeur, aucune unité — explication simple, neutre, non anxiogène. Aucun commentaire sur le poids ni jugement sur des aliments.`,
    );
  }
  lines.push('');
  lines.push('ANALYSES SAISIES ET CLASSÉES PAR ANISSA (statut = son classement) :');
  for (const theme of data.themes || []) {
    lines.push(`\nThème « ${theme.label} » :`);
    const all = [
      ...theme.attention.map((i) => ({ ...i, bucket: 'ressort' })),
      ...theme.rassurer.map((i) => ({ ...i, bucket: 'rassurer' })),
      ...theme.autres.map((i) => ({ ...i, bucket: 'autre' })),
    ];
    for (const item of all) {
      const parts = [`- ${item.label}`];
      if (item.value) parts.push(`: ${item.value}${item.unit ? ` ${item.unit}` : ''}`);
      parts.push(`· statut posé par Anissa : ${item.status_label || 'non renseigné'}`);
      if (item.synthesis) parts.push(`· sa note : ${item.synthesis}`);
      lines.push(parts.join(' '));
    }
  }
  if (data.global_synthesis) {
    lines.push(`\nSynthèse globale écrite par Anissa :\n${data.global_synthesis}`);
  }
  lines.push(
    `\nPrépare les points d'appel. Rappels : ce qui « ressort » = uniquement les statuts À surveiller / Sous-optimal / Élevé posés par Anissa ; ce qui rassure = les statuts Optimal. N'ajoute rien d'autre.`,
  );
  return lines.join('\n');
}

/** Erreur dédiée au scan de sortie (fail-closed). */
export class ForbiddenPhraseError extends Error {
  constructor(phrases) {
    super(`Formulation interdite détectée : ${phrases.join(', ')}`);
    this.name = 'ForbiddenPhraseError';
    this.phrases = phrases;
  }
}

/**
 * Appel principal. Lève si pas assez de données (fail-closed), si l'API
 * échoue, ou si la sortie contient une formulation interdite.
 */
export async function suggestResultsCall(data, { signal = null } = {}) {
  if (!data || !Array.isArray(data.themes) || data.themes.length === 0) {
    throw new Error('Aucune analyse exploitable pour préparer l\'appel.');
  }
  const result = await callClaude({
    system: SYSTEM_PROMPT,
    user: buildResultsCallPrompt(data),
    maxTokens: 1800,
    parseJson: true,
    signal,
  });

  const hits = findOutputViolations(result, { sensitive: data.sensitive === true });
  if (hits.length > 0) {
    throw new ForbiddenPhraseError(hits);
  }
  return result;
}

export { hasEnoughForResultsCall, ClaudeApiError };
